/* eslint-disable react/prop-types */
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { notification } from 'antd';
import cs from 'classnames';
import { flow } from 'lodash/fp';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { StoreState } from '@root/types';
import ErrorBoundary from '@components/shared/ErrorBoundary';
import OnlyInDesktopTablet from '@components/shared/OnlyInDesktopTablet';
import routes from '@root/routes';
import useUserRegisterer from '@components/shared/useUserRegisterer';
import Breadcrumbs from '@components/shared/Breadcrumbs';

import { hasBreadcrumbs, hasCurrentUser as hasCurrentUserSelector } from '@store/selectors';
import contentWidth from '@styles/contentWidth.module.less';
import { withTranslation } from 'react-i18next';
import Navigation from './DeprecatedNavigation';
import AccountSettings from './AccountSettings';
import Building from './Building';
import Homepage from './Homepage';
import Listing from './Listing';
import ListingSearchDeprecated from './ListingSearchDeprecated';
import ListingSearch from './ListingSearch';
import ListingShare from './Listing/ListingShare';
import VtsMarketBuildingShare from './Building/VtsMarketBuildingShare';
import VtsMarketListingShare from './Listing/VtsMarketListingShare';
import VtsMarketLandlordShare from './Landlord/VtsMarketLandlordShare';
import SavedListings from './Saved/Listings';
import SavedSearches from './Saved/Searches';
import SavedBuildings from './Saved/Buildings';
import SavedBuildingOwners from './Saved/BuildingOwners';
import Tourbook from './Tourbook';
import TourbookShare from './Tourbook/TourbookShare';
import Tourbooks from './Tourbooks';
import {
  NewPage as NewTourbookExternalListing,
  EditPage as EditTourbookExternalListing,
} from './TourbookExternalListing';
import {
  NewPage as NewTourbookExternalListingDeprecated,
  EditPage as EditTourbookExternalListingDeprecated,
} from './TourbookExternalListingDeprecated';
import Landlord from './Landlord';
import Layout from './Layout';
import { DesktopUpsellBanner, MobileUpsellBanner } from './UpsellBanner';
import OnboardingModal from './OnboardingModal';

import s from './App.module.less';
import '@styles/index.less';
import NotFound from './404/NotFound';

const {
  building: buildingRoute,
  buildingPreview: buildingPreviewRoute,
  listing: listingRoute,
  listingPreview: listingPreviewRoute,
  listingShare: listingShareRoute,
  users: usersRoute,
  landlords: landlordsRoute,
} = routes;

const connector = connect((state: StoreState) => ({
  hasCurrentUser: hasCurrentUserSelector(state),
  showBreadcrumbContainer: hasBreadcrumbs(state),
}));

type ReduxProps = ConnectedProps<typeof connector>;
type Props = {
  notificationsOnLoad?: { notice?: string; alert?: string };
};

const LayoutWithNoFooter = () => (
  <Layout showFooter={false}>
    <div />
  </Layout>
);

const App = ({
  notificationsOnLoad,
  hasCurrentUser,
  showBreadcrumbContainer,
}: ReduxProps & Props) => {
  const flags = useFlags();
  useEffect(() => {
    if (!notificationsOnLoad) return;

    const { notice, alert } = notificationsOnLoad;

    if (notice) notification.info({ message: notice });
    if (alert) notification.warning({ message: alert });
  }, []);

  useUserRegisterer();

  const { pathname } = useLocation();
  const canSeeBreadcrumbs = hasCurrentUser && !pathname.startsWith('/listing_shares');

  useEffect(() => {
    const setVh = () => {
      // this is because mobile doesn't accept vh as a property correctly
      const vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();

    window.addEventListener('resize', setVh);
  }, []);

  return (
    <ErrorBoundary>
      <div id="allContent" className={s.layout}>
        <OnboardingModal />
        <header className={s.header}>
          <DesktopUpsellBanner />
          {flags['search-redesign'] ? null : <Navigation />}
          <MobileUpsellBanner />
        </header>
        {flags['move-breadcrumbs-to-layout'] ? null : (
          <div
            className={cs(
              contentWidth.contentContainer,
              contentWidth.contentPadding,
              showBreadcrumbContainer && s.breadcrumbsContainer,
            )}
          >
            {canSeeBreadcrumbs ? (
              <OnlyInDesktopTablet>
                <Breadcrumbs />
              </OnlyInDesktopTablet>
            ) : null}
          </div>
        )}
        <Switch>
          <Route path="/office-space/:marketSlug" component={ListingSearchDeprecated} />
          <Route
            path={routes.search(':marketSlug')}
            component={flags['search-redesign'] ? ListingSearch : LayoutWithNoFooter}
          />
          <Route path={routes.savedListings} component={SavedListings} />
          <Route path={routes.savedSearches} component={SavedSearches} />
          <Route path={routes.savedBuildings} component={SavedBuildings} />
          <Route path={routes.savedBuildingOwners} component={SavedBuildingOwners} />
          <Route
            path={routes.tourbookNewExternalListing(':tourbookId')}
            component={
              flags['tourbook-UK-external-listing']
                ? NewTourbookExternalListing
                : NewTourbookExternalListingDeprecated
            }
          />
          <Route
            path={routes.tourbookEditExternalListing(':tourbookId', ':externalListingId')}
            component={
              flags['tourbook-UK-external-listing']
                ? EditTourbookExternalListing
                : EditTourbookExternalListingDeprecated
            }
          />
          <Route path={routes.tourbook(':tourbookId')} component={Tourbook} />
          <Route path={routes.tourbookShare(':tourbookId')} component={TourbookShare} />
          <Route path={routes.tourbooks} component={Tourbooks} />
          <Route
            path={listingPreviewRoute(':listingId')}
            render={props => <Listing {...props} isPreview />}
          />
          <Route
            path={listingRoute(':listingId', ':buildingSlug')}
            render={props => <Listing {...props} isPreview={false} />}
          />
          <Route
            path={listingShareRoute(':encodedData')}
            render={props => <ListingShare {...props} isPreview={false} />}
          />
          <Route
            path={routes.vtsMarketBuildingShare(':buildingSlug')}
            render={props => <VtsMarketBuildingShare {...props} isPreview={false} />}
          />
          <Route
            path={routes.vtsMarketListingShare(':listingId')}
            render={props => <VtsMarketListingShare {...props} isPreview={false} />}
          />
          <Route
            path={buildingPreviewRoute(':buildingSlug')}
            render={props => <Building {...props} isPreview />}
          />
          <Route
            path={buildingRoute(':buildingSlug')}
            render={props => <Building {...props} isPreview={false} />}
          />
          <Route render={() => <AccountSettings />} path={usersRoute.edit(':accountSettingsTab')} />
          <Route path={landlordsRoute(':slug')} component={Landlord} />
          <Route path={routes.vtsMarketLandlordShare(':slug')} component={VtsMarketLandlordShare} />
          <Route exact path="/" component={Homepage} />
          <Route path="*" component={NotFound} />
        </Switch>
      </div>
    </ErrorBoundary>
  );
};

export default flow(withTranslation(), connector)(App);
